'use client';

import React, { useState } from 'react';

type CurrencyCode = 'CAD' | 'USD';

interface CurrencySelectorProps {
  currentCurrency: CurrencyCode;
  onChange: (currency: CurrencyCode) => void;
}

/**
 * A minimal floating currency selector component that's always visible
 */
const CurrencySelector: React.FC<CurrencySelectorProps> = ({ currentCurrency, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);

  const currencies: { code: CurrencyCode; name: string; symbol: string }[] = [
    { code: 'CAD', name: 'CAD', symbol: 'CA$' },
    { code: 'USD', name: 'USD', symbol: '$' },
  ];

  const handleCurrencySelect = (currency: CurrencyCode) => {
    onChange(currency);
    setIsOpen(false);
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  // Get current currency details
  const currentCurrencyObj = currencies.find(c => c.code === currentCurrency) || currencies[0];

  return (
    <div className="fixed right-6 bottom-6 z-50">
      <div className="relative">
        {/* Current currency button */}
        <button
          onClick={toggleDropdown}
          className="bg-[#121212]/80 backdrop-blur-md text-gray-300 border border-gray-800 rounded-full p-2 px-4 shadow-xl hover:bg-gray-800 transition-colors flex items-center"
          aria-label="Select currency"
          title="Change currency"
        >
          {/* Currency icon */}
          <svg 
            className="w-4 h-4 mr-1.5" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          >
            <path d="M17 4H7M17 4l-3.5 3.5M17 4l-3.5-3.5"/>
            <path d="M7 20h10M7 20l3.5-3.5M7 20l3.5 3.5"/>
            <path d="M19 12H5"/>
          </svg>
          
          <span className="text-sm font-medium">{currentCurrencyObj.code}</span>
          <svg 
            className={`w-4 h-4 ml-1 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24" 
            xmlns="http://www.w3.org/2000/svg"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {/* Currency dropdown */}
        {isOpen && (
          <div className="absolute bottom-full mb-2 right-0 bg-[#121212]/90 backdrop-blur-md text-gray-300 border border-gray-800 rounded-lg p-2 shadow-xl min-w-[120px]">
            {currencies.map((currency) => (
              <button
                key={currency.code}
                onClick={() => handleCurrencySelect(currency.code)}
                className={`w-full text-left px-3 py-2 text-sm rounded-md transition-colors flex items-center justify-between ${
                  currentCurrency === currency.code
                    ? 'bg-blue-600 text-white'
                    : 'hover:bg-gray-700'
                }`}
              >
                <span>{currency.name}</span>
                <span className="ml-2 text-xs opacity-80">{currency.symbol}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CurrencySelector; 